import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class CartUpsell extends Component {
  #current = 0;
  #isDragging = false;
  #startX = 0;
  #currentTranslateX = 0;
  #dragListenersAttached = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Add drag event listeners
    this.refs.slides?.forEach(slide => {
      slide.addEventListener('mousedown', this.#onDragStart);
      slide.addEventListener('touchstart', this.#onDragStart);
    });

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.refs.slides?.forEach((slide) => {
      slide.removeEventListener('mousedown', this.#onDragStart);
      slide.removeEventListener('touchstart', this.#onDragStart);
    });
    this.#removeDragListeners();
  }

  // Drag start
  #onDragStart = (e) => {
    this.#isDragging = true;
    this.#startX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    this.#addDragListeners();
    this.suspend(); // optionally pause autoplay during drag
  };

  // Drag move
  #onDragMove = (e) => {
    if (!this.#isDragging) return;
    const currentX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const deltaX = currentX - this.#startX;

    // Optional: implement real-time slide drag effect here
    // For simplicity, this example just tracks deltaX
    this.#currentTranslateX = deltaX;
  };

  // Drag end
  #onDragEnd = () => {
    if (!this.#isDragging) return;
    this.#isDragging = false;

    // Decide if slide should change based on drag distance
    const threshold = 50; // pixels
    if (this.#currentTranslateX > threshold) {
      this.previous(); // swipe right -> previous slide
    } else if (this.#currentTranslateX < -threshold) {
      this.next(); // swipe left -> next slide
    }

    // Reset translation
    this.#currentTranslateX = 0;
    this.#removeDragListeners();

    this.resume(); // resume autoplay if needed
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #addDragListeners() {
    if (this.#dragListenersAttached) return;
    this.#dragListenersAttached = true;
    document.addEventListener('mousemove', this.#onDragMove);
    document.addEventListener('touchmove', this.#onDragMove, { passive: true });
    document.addEventListener('mouseup', this.#onDragEnd);
    document.addEventListener('touchend', this.#onDragEnd, { passive: true });
  }

  #removeDragListeners() {
    if (!this.#dragListenersAttached) return;
    this.#dragListenersAttached = false;
    document.removeEventListener('mousemove', this.#onDragMove);
    document.removeEventListener('touchmove', this.#onDragMove);
    document.removeEventListener('mouseup', this.#onDragEnd);
    document.removeEventListener('touchend', this.#onDragEnd);
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsell);
}
